import { useEffect, useMemo, useState } from "react";
import { Search, MessageCircle, ChevronRight, Star } from "lucide-react";
import { Input } from "../components/ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import api from "../lib/api";
import { CLINIC, waLink } from "../lib/clinic";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";
import { breadcrumb, graph, AUTHOR_REF, PUBLISHER_REF, SPEAKABLE } from "../lib/schema";
import SymptomCheckerTeaser from "../components/site/SymptomCheckerTeaser";

const FAQ = () => {
  usePageMeta({
    title: "FAQ | Dr. Shantanu Kumar Gupta, Varanasi Eye Specialist",
    path: "/faq",
    description: "Answers to common patient questions on cataract surgery, retina injections, diabetic eye care, insurance and clinic visits at Shantanu Netralaya.",
  });
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState("All");

  useEffect(() => { api.get("/faqs").then((r) => setItems(r.data)); }, []);

  // FAQPage schema for AI/rich results
  const faqSchema = useMemo(() => {
    if (!items.length) return null;
    return graph([
      {
        "@type": "FAQPage",
        "@id": "https://drshantanugupta.com/faq#faq",
        // Voice search: Google Assistant reads the H1 + H2 chapter headings.
        speakable: SPEAKABLE,
        // Link this FAQPage to our MedicalClinic entity so search engines
        // can bind these answers to the GMB Business Profile.
        about: { "@id": "https://drshantanugupta.com/#hospital" },
        publisher: PUBLISHER_REF,
        // Doctor-authored FAQ — the whole page is authored by Dr. Shantanu.
        // Corporate hospital FAQ pages almost never carry a named author,
        // which is why Google's helpful-content system deprioritises them.
        author: AUTHOR_REF,
        // Bind every individual Q&A to the doctor as author + Business as
        // publisher — makes each answer eligible for AI-search citation
        // ("According to Dr. Shantanu Gupta of Shantanu Netralaya…").
        mainEntity: items.slice(0, 30).map((f) => ({
          "@type": "Question",
          name: f.q,
          answerCount: 1,
          acceptedAnswer: {
            "@type": "Answer",
            text: f.a,
            author: AUTHOR_REF,
            publisher: PUBLISHER_REF,
            dateModified: f.updated_at || f.created_at || undefined,
          },
        })),
      },
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "FAQ", path: "/faq" },
      ]),
    ]);
  }, [items]);
  usePageJsonLd(faqSchema);

  const categories = useMemo(
    () => ["All", ...Array.from(new Set(items.map((i) => i.category).filter(Boolean)))],
    [items]
  );

  const countByCat = useMemo(() => {
    const m = { All: items.length };
    items.forEach((i) => { m[i.category] = (m[i.category] || 0) + 1; });
    return m;
  }, [items]);

  const filtered = useMemo(
    () => items.filter((it) => {
      const catOk = active === "All" || it.category === active;
      const q = query.trim().toLowerCase();
      const qOk = !q || (it.q || "").toLowerCase().includes(q) || (it.a || "").toLowerCase().includes(q);
      return catOk && qOk;
    }),
    [items, active, query]
  );

  // Group filtered FAQs by category so long lists remain scannable
  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach((f) => {
      const key = f.category || "General";
      (groups[key] ||= []).push(f);
    });
    return groups;
  }, [filtered]);

  return (
    <div data-testid="faq-page">
      <section className="pt-20 pb-10 container-page">
        <div className="overline mb-4">Frequently Asked</div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance max-w-3xl">
          Real answers to the questions patients actually ask.
        </h1>
        <p className="mt-5 max-w-2xl prose-elegant">
          Curated by Dr. Shantanu Kumar Gupta. Search across all topics or jump to a category below.
        </p>
      </section>

      <section className="container-page pb-4">
        <div className="relative max-w-xl">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search FAQ (try 'cataract cost' or 'retina injection')"
            className="pl-9 h-11"
            data-testid="faq-search"
          />
        </div>
      </section>

      <section className="container-page py-10 grid gap-10 lg:grid-cols-[260px_1fr]" data-testid="faq-list">
        {/* Sticky category rail (desktop) / horizontal chips (mobile) */}
        <aside className="lg:sticky lg:top-24 self-start">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent))] mb-4 hidden lg:block">Topics</div>
          <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-1 lg:pb-0 -mx-5 lg:mx-0 px-5 lg:px-0 snap-x lg:snap-none">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                data-testid={`faq-cat-${c.replace(/\s/g, "-").toLowerCase()}`}
                className={`shrink-0 lg:w-full snap-start text-left px-4 py-2.5 rounded-lg text-sm border transition-all flex items-center justify-between gap-3 ${
                  active === c
                    ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]"
                    : "border-border text-[hsl(var(--foreground))]/75 hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]"
                }`}
              >
                <span className="truncate">{c}</span>
                <span className={`text-[11px] tabular-nums ${active === c ? "text-white/80" : "text-muted-foreground"}`}>
                  {countByCat[c] || 0}
                </span>
              </button>
            ))}
          </div>

          <div className="hidden lg:block mt-8 rounded-xl border border-border p-5 bg-[hsl(var(--secondary))]/40">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent))] mb-2">Can&apos;t find your answer?</div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Dr. Shantanu personally reviews every WhatsApp enquiry — usually within a few hours.
            </p>
            <a
              href={waLink("Hi Dr. Shantanu, I have a question that is not covered in your FAQ.")}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="btn-primary mt-4 !py-2.5 !px-4 text-xs w-full"
              data-testid="faq-ask-btn"
            >
              <MessageCircle size={14} /> Ask on WhatsApp
            </a>
            <a
              href={CLINIC.googleReviewsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-ask-on-google-desktop"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-md border border-border px-4 py-2.5 text-xs hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--accent))] transition-colors"
            >
              <Star size={13} /> Or ask a question on Google
            </a>
          </div>
        </aside>

        {/* Q&A column */}
        <div>
          {filtered.length === 0 && (
            <div className="py-16 text-center border border-dashed border-border rounded-xl text-muted-foreground">
              No FAQ matches your search. Try a different keyword or browse a category.
            </div>
          )}

          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat} className="mb-10 last:mb-0" data-testid={`faq-group-${cat.replace(/\s/g, "-").toLowerCase()}`}>
              <div className="flex items-center gap-3 mb-4">
                <h2 className="font-serif text-2xl sm:text-3xl">{cat}</h2>
                <ChevronRight size={16} className="text-[hsl(var(--accent))]" />
                <span className="text-xs uppercase tracking-[0.22em] text-muted-foreground">{list.length} question{list.length === 1 ? "" : "s"}</span>
              </div>
              <div className="h-[2px] w-12 bg-[hsl(var(--accent))] mb-4" />
              <Accordion type="single" collapsible className="border border-border rounded-xl overflow-hidden bg-card">
                {list.map((f, i) => (
                  <AccordionItem key={`${cat}-${i}`} value={`${cat}-${i}`} className="border-b last:border-b-0" data-testid={`faq-item-${cat.replace(/\s/g, "-").toLowerCase()}-${i}`}>
                    <AccordionTrigger className="text-left font-medium px-5 py-4 hover:bg-[hsl(var(--secondary))]/50 [&[data-state=open]]:bg-[hsl(var(--secondary))]/50">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="px-5 pb-5 text-[15px] leading-[1.75] text-[hsl(var(--foreground))]/80 whitespace-pre-line">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}

          {/* Mobile ask CTA */}
          <div className="lg:hidden mt-8 rounded-xl border border-border p-5 bg-[hsl(var(--secondary))]/40">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent))] mb-2">Still need help?</div>
            <p className="text-sm text-muted-foreground leading-relaxed">Dr. Shantanu personally reviews every enquiry.</p>
            <a
              href={waLink("Hi Dr. Shantanu, I have a question that is not covered in your FAQ.")}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="btn-primary mt-4 !py-2.5 !px-4 text-xs w-full"
            >
              <MessageCircle size={14} /> Ask on WhatsApp
            </a>
            <a
              href={CLINIC.googleReviewsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-ask-on-google-mobile"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-md border border-border px-4 py-2.5 text-xs hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--accent))] transition-colors"
            >
              <Star size={13} /> Or ask a question on Google
            </a>
          </div>
          <div className="mt-10">
            <SymptomCheckerTeaser context="faq" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
